// import axios from 'axios';

import moment from 'moment';
import 'moment/locale/en-gb';

import { isValidNumber } from 'libphonenumber-js';

import get from 'lodash.get';

import { hasFormbuilderQuestions, getFormbuilderQuestions } from './formBuilderHelper';
import { nodeTypes, addresFields, additionalAnswerValidation, addressFieldsValidation } from '../constants/formbuilder';

import { REQUIRED_MESSAGE, EMAIL_INVALID_MESSAGE, DATE_INVALID_MESSAGE, PHONE_INVALID_MESSAGE, getMaximumLengthMessage } from '../constants/errorMessages';
import { DEFAULT_DATE_FORMAT } from '../constants';

export const isValid = (validationModel, value) => {
    if (!validationModel) {
        return true;
    }

    if (validationModel.required && !isRequiredValid(value)) {
        return false;
    }

    if (validationModel.email && !isEmailValid(value)) {
        return false;
    }

    if (validationModel.amount && !isAmountValid(value, validationModel.maxValue)) {
        return false;
    }

    if (validationModel.formBuilderAnswers && !isFormBuilderAnswersValid(value)) {
        return false;
    }

    if (validationModel.formbuilderAddressRequired && !isFormbuilderAddressRequiredValid(value)) {
        return false;
    }

    if (validationModel.formbuilderAddress && !isFormbuilderAddressValid(value)) {
        return false;
    }

    if (validationModel.phone && !isPhoneValid(value)) {
        return false;
    }

    if (validationModel.date && !isDateStringValid(value)) {
        return false;
    }

    if (validationModel.maxLengthValidation && !isMaxLengthValid(value, validationModel.maxLength)) {
        return false;
    }

    return true;
}

export const getValidationErrorMessage = (validationModel, value) => {
    if (!validationModel) {
        return "";
    }

    if (validationModel.required && !isRequiredValid(value)) {
        return REQUIRED_MESSAGE;
    }

    if (validationModel.maxLengthValidation && !isMaxLengthValid(value, validationModel.maxLength)) {
        return getMaximumLengthMessage(validationModel.maxLength);
    }

    if (validationModel.email && !isEmailValid(value)) {
        return EMAIL_INVALID_MESSAGE;
    }

    if (validationModel.phone && !isPhoneValid(value)) {
        return PHONE_INVALID_MESSAGE;
    }

    if (validationModel.date && !isDateStringValid(value)) {
        return DATE_INVALID_MESSAGE;
    }

    return "";
}

export const isAllFieldsValid = (fieldsWithValidationModels) => {
    if (!fieldsWithValidationModels || !Array.isArray(fieldsWithValidationModels)) {
        return true;
    }

    const validationResults = fieldsWithValidationModels.map((item) => {
        if (!item) {
            return true;
        }
        return isValid(item.validationModel, item.value);
    });

    return validationResults.filter(item => item === false).length === 0;
}

export const isRequiredValid = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (typeof value === 'string') {
        return value.trim() ? true : false;
    }

    return value ? true : false;
}

export const isMaxLengthValid = (value, maxLength) => {
    if (!value) {
        return true;
    }

    if (value) {
        return value.length <= maxLength;
    }

    return false;
}

export const isEmailValid = (email) => {
    if (email && email.trim()) {
        const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return emailRegex.test(email.toLowerCase());
    }

    return true;
}

export const isPhoneValid = (phone) => {
    if (phone) {
        if (phone.startsWith('0')) {
            return isValidNumber(phone, 'AU');
        }

        return isValidNumber(phone);
    }

    return true;
}

export const isDateStringValid = (date) => {
    if (date) {
        const dateRegex = /\d\d\/\d\d\/\d\d\d\d/;
        return dateRegex.test(date) && moment(date, DEFAULT_DATE_FORMAT).isValid();
    }

    return true;
}

export const isFormBuilderAnswersValid = (formBuilderModel) => {

    if (!hasFormbuilderQuestions(formBuilderModel)) {
        return true;
    }

    const validationResults = getFormbuilderQuestions(formBuilderModel).map(item => {
        if (item.props.required && !item.answer) {
            return false;
        }

        const answerValidation = additionalAnswerValidation[item.type];
        if (answerValidation && !isValid(answerValidation, item.answer)) {
            return false;
        }

        if (item.type === nodeTypes.ADDRESS && ((item.props.required && !isFormbuilderAddressRequiredValid(item.answer)) || !isFormbuilderAddressValid(item.answer))) {
            return false;
        }

        return true;
    });

    return validationResults.every(x => x === true);
}

export const isFormbuilderAddressRequiredValid = (value) => {
    if (!value) {
        return false;
    }

    const addressFieldsValidations = Object.keys(addresFields).map(field => {
        const fieldPath = addresFields[field];

        return isRequiredValid(value[fieldPath])
    });

    return addressFieldsValidations.every(x => x === true);
}

export const isFormbuilderAddressValid = (value) => {
    if (!value) {
        return true;
    }

    const addressFieldsValidations = Object.keys(addresFields).map(field => {
        const fieldPath = addresFields[field];
        const maxLength = get(addressFieldsValidation, `${fieldPath}.maxLength`);

        return isMaxLengthValid(value[fieldPath], maxLength);
    });

    return addressFieldsValidations.every(x => x === true);
}

export const isEmailExists = (email, onValidationChange) => {
    // return axios.post('email/api/validate', {
    //     email: email
    // }).then((response) => {
    //     onValidationChange(response.data.isValid);
    // });
}

export const isAmountValid = (value, maxValue) => {
    if (!value) {
        return true;
    }

    const isNumberPositive = !isNaN(value) && value > 0;

    if (!isNumberPositive) {
        return false;
    }

    if (maxValue) {
        return isValueLessThanMaxValue(value, maxValue);
    }

    return true;
}

const isValueLessThanMaxValue = (value, maxValue) => {
    return value <= maxValue;
}

export const isValidationFormValid = (validationFormValid) => {
    return Object.entries(validationFormValid).every(([_key, value]) => value);
};